// src/common/userUtils.js
import { logDebug, logError } from '@/utils/logHelper';

// ✅ 상수 분리 (외부에서 참조 가능)
export const STORAGE_KEYS = Object.freeze({
  USER_INFO: '__user__Info__',
  ACCESS_TOKEN: '__user__access__token__',
  REFRESH_TOKEN: '__user__refresh__token__',
});

const userUtils = {
  // 레거시 호환
  storageKeyUser: STORAGE_KEYS.USER_INFO,
  storageKeyAccessToken: STORAGE_KEYS.ACCESS_TOKEN,
  storageKeyRefreshToken: STORAGE_KEYS.REFRESH_TOKEN,

  /* =========================================================
   * Access Token
   * ======================================================= */
  setAccessToken(accessToken) {
    if (!accessToken) {
      logDebug('setAccessToken: 빈 토큰, 저장 건너뜀');
      return;
    }
    sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  },

  clearAccessToken() {
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  getAccessToken() {
    return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
  },

  hasAccessToken() {
    return !!this.getAccessToken();
  },

  /* =========================================================
   * Refresh Token
   * ======================================================= */
  setRefreshToken(refreshToken) {
    if (!refreshToken) {
      logDebug('setRefreshToken: 빈 토큰, 저장 건너뜀');
      return;
    }
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  },

  clearRefreshToken() {
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  getRefreshToken() {
    return sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
  },

  hasRefreshToken() {
    return !!this.getRefreshToken();
  },

  /* =========================================================
   * User Info
   * ======================================================= */
  setUserInfo(userInfo) {
    if (!userInfo) {
      logDebug('setUserInfo: 빈 정보, 저장 건너뜀');
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
    } catch (err) {
      logError('setUserInfo 실패:', err);
    }
  },

  clearUserInfo() {
    sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
  },

  getUserInfo() {
    const userStr = sessionStorage.getItem(STORAGE_KEYS.USER_INFO);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (err) {
      logError('getUserInfo JSON 파싱 실패:', err);
      return null;
    }
  },

  /**
   * 사용자 SUID 반환
   * @returns {string|null}
   */
  getUserSuid() {
    return this.getUserInfo()?.userSuid || null;
  },

  /**
   * 사용자 이메일 반환
   * @returns {string|null}
   */
  getUserEmail() {
    return this.getUserInfo()?.userEmail || null;
  },

  /**
   * 사용자 역할 목록 반환
   * @returns {string[]}
   */
  getUserRoles() {
    return this.getUserInfo()?.roles || [];
  },

  /**
   * 특정 역할 보유 여부
   * @param {string} role - 확인할 역할 (예: 'ROLE_ADMIN')
   * @returns {boolean}
   */
  hasRole(role) {
    if (!role) return false;
    return this.getUserRoles().includes(role);
  },

  /**
   * 관리자 여부
   * @returns {boolean}
   */
  isAdmin() {
    return this.hasRole('ROLE_ADMIN');
  },

  /**
   * 벤더 여부
   * @returns {boolean}
   */
  isVendor() {
    return this.hasRole('ROLE_VENDOR');
  },

  /* =========================================================
   * 통합 메서드
   * ======================================================= */

  /**
   * 모든 인증 정보 삭제
   */
  clearAll() {
    this.clearAccessToken();
    this.clearRefreshToken();
    this.clearUserInfo();
    logDebug('userUtils: 모든 인증 정보 삭제 완료');
  },

  /**
   * 로그인 상태 확인
   * @returns {boolean}
   */
  isLoggedIn() {
    return this.hasAccessToken() && !!this.getUserSuid();
  },

  /**
   * 서버 응답으로 로그인 정보 저장
   * @param {object} serverResponse - 서버 응답
   */
  setLoginResponse(serverResponse) {
    // 기존 정보 먼저 삭제
    this.clearAll();

    if (!serverResponse) {
      logError('setLoginResponse: 빈 응답');
      return;
    }

    const { 
      accessToken, 
      refreshToken, 
      userSuid, 
      userEmail, 
      roles 
    } = serverResponse;

    // 토큰 저장
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);

    // 사용자 정보 저장
    const userInfo = {
      userSuid,
      userEmail,
      roles: Array.isArray(roles) ? roles : [],
      // ✅ 추가: 로그인 시간 기록
      loginAt: new Date().toISOString(),
    };

    this.setUserInfo(userInfo);
    logDebug('setLoginResponse 완료:', { userSuid, userEmail, roles });
  },

  /**
   * 디버깅용: 현재 상태 출력
   * @returns {object}
   */
  getDebugInfo() {
    return {
      hasAccessToken: this.hasAccessToken(),
      hasRefreshToken: this.hasRefreshToken(),
      userSuid: this.getUserSuid(),
      userEmail: this.getUserEmail(),
      roles: this.getUserRoles(),
      isLoggedIn: this.isLoggedIn(),
    };
  },
};

export default userUtils;